/**
 * Coach/partner sharing hooks (T16.5 / ISSUE-081) — web client.
 *
 * Owner endpoints are premium-gated server-side (feature=coach_sharing);
 * grantee endpoints (accept, list views, read week) are not gated — a coach
 * does not pay to view. The shared week view is aggregate-only: trend,
 * adherence calendar, target history, weekly summary. Chat and raw logs are
 * never returned.
 */
import type {
  CoachLink,
  CoachViewSummary,
  CreateCoachLink,
  SharedWeekView,
} from '@nutians/shared/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api.js';

export const COACH_LINKS_KEY = ['sharing', 'coach-links'] as const;
export const COACH_VIEWS_KEY = ['sharing', 'coach-views'] as const;
export const sharedWeekKey = (ownerUserId: string) =>
  ['sharing', 'coach-views', ownerUserId, 'week'] as const;

/** Owner: outgoing links I have created. */
export function useCoachLinks() {
  return useQuery<CoachLink[]>({
    queryKey: COACH_LINKS_KEY,
    queryFn: async () => {
      const { data, error } = await api.GET('/me/sharing/coach-links');
      if (error || !data) throw new Error('GET /me/sharing/coach-links failed');
      return (data as { links: CoachLink[] }).links;
    },
    staleTime: 30_000,
    retry: false,
  });
}

/** Owner: create an invite. */
export function useCreateCoachLink() {
  const qc = useQueryClient();
  return useMutation<CoachLink, Error, CreateCoachLink>({
    mutationFn: async (body) => {
      const { data, error } = await api.POST('/me/sharing/coach-links', { body });
      if (error || !data) throw new Error('POST /me/sharing/coach-links failed');
      return data as CoachLink;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: COACH_LINKS_KEY });
    },
  });
}

/** Owner: revoke a link (immediate consent withdrawal). */
export function useRevokeCoachLink() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      const { error } = await api.DELETE('/me/sharing/coach-links/{id}', {
        params: { path: { id } },
      });
      if (error) throw new Error('DELETE /me/sharing/coach-links failed');
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: COACH_LINKS_KEY });
    },
  });
}

/** Grantee: list owners sharing their week with me. */
export function useCoachViews() {
  return useQuery<CoachViewSummary[]>({
    queryKey: COACH_VIEWS_KEY,
    queryFn: async () => {
      const { data, error } = await api.GET('/me/sharing/coach-views');
      if (error || !data) throw new Error('GET /me/sharing/coach-views failed');
      return (data as { views: CoachViewSummary[] }).views;
    },
    staleTime: 30_000,
    retry: false,
  });
}

/** Grantee: accept a pending invite by token. */
export function useAcceptCoachLink() {
  const qc = useQueryClient();
  return useMutation<CoachLink, { status?: number }, string>({
    mutationFn: async (token) => {
      const { data, error, response } = await api.POST('/me/sharing/coach-links/accept', {
        body: { token },
      });
      if (error || !data) {
        const err: { status?: number } = { status: response?.status };
        throw err;
      }
      return data as CoachLink;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: COACH_VIEWS_KEY });
    },
  });
}

/** Grantee: read a shared week view for one owner. */
export function useSharedWeek(ownerUserId: string | null) {
  return useQuery<SharedWeekView>({
    queryKey: ownerUserId ? sharedWeekKey(ownerUserId) : ['sharing', 'coach-views', 'none', 'week'],
    enabled: !!ownerUserId,
    queryFn: async () => {
      const { data, error } = await api.GET('/me/sharing/coach-views/{ownerUserId}/week', {
        params: { path: { ownerUserId: ownerUserId as string } },
      });
      if (error || !data) throw new Error('GET /me/sharing/coach-views/{ownerUserId}/week failed');
      return data as SharedWeekView;
    },
    staleTime: 30_000,
    retry: false,
  });
}
